import { action, makeObservable, observable } from 'mobx';

import getContentGenres from '@smart-tv/utils/kmm/getContentGenres';
import { ContentListProviders } from '@smart-tv/typings/content';

import { StripesBaseStore } from './store-stripes-base';
import { PageType } from '../_utils/types';
import { getAreaForPageType } from '@smart-tv/utils/kmm/helpers';
import { setFocus } from '@noriginmedia/norigin-spatial-navigation';

interface StripesParams {
  favoritesListId?: number;
  universeId?: string;
  pageType: PageType;
}

class StripesStore extends StripesBaseStore {
  params: StripesParams | null = null;

  constructor() {
    super();
    makeObservable(this, {
      params: observable,
      setParams: action,
      fetchUiElements: action,
      refresh: action,
      refetchUpdateOnFavoriteStripes: action
    });
  }

  setParams = (params: StripesParams) => {
    this.params = params;
  };

  async fetchUiElements(): Promise<void> {
    if (!this.params) return;
    if (this.params.favoritesListId === undefined) return; // Ensure favoritesListId is defined before fetching UI elements

    const { favoritesListId, universeId } = this.params;
    const isUniverse = this.params.pageType === PageType.Universe && !!universeId;

    const area = getAreaForPageType(
      this.params.pageType,
      universeId ? Number(universeId) : undefined
    );

    try {
      const universeGenres = isUniverse
        ? await getContentGenres(Number(universeId), ContentListProviders.Subscribed)
        : [];

      await this.subscribeUiElementsBase(favoritesListId, area, async (uiElements, isStale) => {
        const elements = isUniverse ? uiElements.concat(universeGenres) : uiElements;

        const enrichedUiElements = await Promise.all(
          elements.map((uiElement) => this.enrichUiElement(uiElement, area, favoritesListId))
        );

        const enrichedUiElementsWithItems = enrichedUiElements.filter(
          (uiElement) => uiElement.hasItems
        );

        if (isStale()) return;
        this.setUiElements(enrichedUiElementsWithItems);
      });
    } finally {
      this.setLoading(false);
    }
  }

  refresh = async (focusKey?: string): Promise<void> => {
    this.resetUiElements();
    await this.fetchUiElements();
    if (focusKey) {
      setFocus(focusKey);
    }
  };

  async refetchUpdateOnFavoriteStripes(): Promise<void> {
    // Override to pass favoritesListId from params
    await super.refetchUpdateOnFavoriteStripes(this.params?.favoritesListId);
  }
}

export default StripesStore;
